import React, { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import {
    createConcept,
    updateConcept,
    deleteConcept,
    initializeConcepts,
} from '../Services/payroll.services';

const EMPTY_FORM = {
    codigo: '',
    nombre: '',
    naturaleza: 'deduccion',
    tipoCalculo: 'porcentaje',
    valor: 0,
    baseCalculo: 'salario',
    activo: true,
};

/**
 * Modal CRUD de conceptos parametrizables (devengados / deducciones).
 * Props:
 *  - open, onOpenChange
 *  - concepts: lista actual
 *  - onChanged: callback para refrescar la lista en el padre
 */
const ConfigureConceptsModal = ({ open, onOpenChange, concepts = [], onChanged }) => {
    const { toast } = useToast();
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
    };

    const handleEdit = (concept) => {
        setEditingId(concept._id);
        setForm({
            codigo: concept.codigo || '',
            nombre: concept.nombre || '',
            naturaleza: concept.naturaleza || 'deduccion',
            tipoCalculo: concept.tipoCalculo || 'porcentaje',
            valor: concept.valor ?? 0,
            baseCalculo: concept.baseCalculo || 'salario',
            activo: concept.activo !== false,
        });
    };

    const handleSave = async () => {
        if (!form.nombre.trim()) {
            toast({ title: 'Error', description: 'El nombre es requerido.', variant: 'destructive' });
            return;
        }
        try {
            setSaving(true);
            const payload = { ...form, valor: Number(form.valor) || 0 };
            const res = editingId
                ? await updateConcept(editingId, payload)
                : await createConcept(payload);
            if (res?.state) {
                toast({ title: 'Éxito', description: editingId ? 'Concepto actualizado.' : 'Concepto creado.' });
                resetForm();
                onChanged?.();
            } else {
                throw new Error(res?.message || 'Error');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error?.response?.data?.message || error.message || 'No se pudo guardar el concepto.',
                variant: 'destructive',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await deleteConcept(id);
            if (res?.state) {
                toast({ title: 'Éxito', description: 'Concepto eliminado.' });
                if (editingId === id) resetForm();
                onChanged?.();
            } else {
                throw new Error(res?.message || 'Error');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error?.response?.data?.message || 'No se pudo eliminar el concepto.',
                variant: 'destructive',
            });
        }
    };

    const handleInitialize = async () => {
        try {
            const res = await initializeConcepts();
            if (res?.state) {
                toast({ title: 'Éxito', description: 'Conceptos por defecto cargados.' });
                onChanged?.();
            }
        } catch (error) {
            toast({ title: 'Error', description: 'No se pudieron inicializar los conceptos.', variant: 'destructive' });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Configuración de Conceptos de Nómina</DialogTitle>
                    <DialogDescription>
                        Define los devengados (suman) y deducciones (restan): Salud, Pensión, Seguros, Primas, etc.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Formulario */}
                    <div className="grid gap-3 p-4 border rounded-lg bg-muted/50">
                        <h3 className="font-semibold">{editingId ? 'Editar Concepto' : 'Nuevo Concepto'}</h3>
                        <div className="grid gap-3 md:grid-cols-3">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Código</label>
                                <Input
                                    placeholder="Ej: 210"
                                    value={form.codigo}
                                    onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium mb-1 block">Nombre</label>
                                <Input
                                    placeholder="Ej: Salud"
                                    value={form.nombre}
                                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Naturaleza</label>
                                <Select value={form.naturaleza} onValueChange={(v) => setForm({ ...form, naturaleza: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="devengado">Devengado (+)</SelectItem>
                                        <SelectItem value="deduccion">Deducción (−)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Tipo de cálculo</label>
                                <Select value={form.tipoCalculo} onValueChange={(v) => setForm({ ...form, tipoCalculo: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
                                        <SelectItem value="fijo">Valor fijo ($)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">
                                    {form.tipoCalculo === 'porcentaje' ? 'Porcentaje (%)' : 'Valor ($)'}
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.valor}
                                    onChange={(e) => setForm({ ...form, valor: e.target.value })}
                                />
                            </div>
                            {form.tipoCalculo === 'porcentaje' && (
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Base de cálculo</label>
                                    <Select value={form.baseCalculo} onValueChange={(v) => setForm({ ...form, baseCalculo: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="salario">Salario mensual</SelectItem>
                                            <SelectItem value="totalDevengado">Total devengado</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleSave} disabled={saving}>
                                <Plus className="mr-2 h-4 w-4" />
                                {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Agregar'}
                            </Button>
                            {editingId && (
                                <Button variant="ghost" onClick={resetForm}>Cancelar edición</Button>
                            )}
                        </div>
                    </div>

                    {/* Lista */}
                    <div className="flex justify-between items-center">
                        <h3 className="font-semibold">Conceptos Registrados ({concepts.length})</h3>
                        {concepts.length === 0 && (
                            <Button variant="outline" size="sm" onClick={handleInitialize}>
                                Cargar conceptos por defecto
                            </Button>
                        )}
                    </div>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Código</TableHead>
                                    <TableHead>Nombre</TableHead>
                                    <TableHead>Naturaleza</TableHead>
                                    <TableHead>Valor</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {concepts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center h-24 text-muted-foreground">
                                            No hay conceptos registrados
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    concepts.map((c) => (
                                        <TableRow key={c._id}>
                                            <TableCell>{c.codigo || '—'}</TableCell>
                                            <TableCell className="font-medium">{c.nombre}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={c.naturaleza === 'devengado' ? 'border-green-500 text-green-600' : 'border-red-500 text-red-600'}>
                                                    {c.naturaleza === 'devengado' ? 'Devengado +' : 'Deducción −'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {c.tipoCalculo === 'porcentaje' ? `${c.valor}%` : `$${Number(c.valor).toLocaleString('es-CO')}`}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(c._id)}>
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ConfigureConceptsModal;
